import React from "react";
import { Box, Text } from "@mantine/core";
import styles from "./WorkInProgressCover.module.css";

interface WorkInProgressCoverProps {
  /** The real mockup layout to render greyed underneath the cover. */
  children: React.ReactNode;
  /** Overlay label (default "Work in progress"). */
  label?: string;
  className?: string;
}

/**
 * Reusable WIP cover (loyalty FE1 §0). Wraps a section/tab/page: renders the
 * real layout underneath with a translucent overlay + centered "Work in
 * progress" label. Everything under the cover is force-disabled:
 *
 * - the content is wrapped in a native `<fieldset disabled>` so every input
 *   and button inside is disabled regardless of its own props;
 * - `pointer-events` are cut on the content and the overlay intercepts all
 *   clicks, so no handler (and no network call) can fire from covered
 *   content;
 * - `aria-hidden` keeps the static preview out of the accessibility tree.
 *
 * Covered content must be static — do not mount components that fetch.
 */
export default function WorkInProgressCover({
  children,
  label = "Work in progress",
  className,
}: WorkInProgressCoverProps) {
  return (
    <Box className={`${styles.root}${className ? ` ${className}` : ""}`}>
      {/* fieldset is the one native element that can force-disable an entire
          subtree of form controls; Mantine has no wrapper for it. */}
      <fieldset disabled className={styles.content} aria-hidden>
        {children}
      </fieldset>
      <Box className={styles.overlay} data-testid="wip-cover-overlay">
        <Text component="span" className={styles.label}>
          {label}
        </Text>
      </Box>
    </Box>
  );
}
